import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface InsightRow {
  readonly insightId: string;
  readonly insightCode: string;
  readonly title: string;
  readonly insightType: string;
  readonly sourceType: string;
  readonly severity: string;
  readonly confidence: string | null;
  readonly recommendation: string | null;
  readonly isAdvisory: boolean;
  readonly status: string;
}

const INSIGHT_TYPES = ['TREND', 'PERFORMANCE', 'RISK', 'COST', 'OPTIMIZATION', 'FORECAST', 'STRATEGIC'];
const SOURCE_TYPES = ['DATASET', 'ANALYTICAL_DATASET', 'FORECAST', 'SCENARIO', 'QUALITY'];
const SEVERITIES = ['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const STATUSES = ['NEW', 'ACKNOWLEDGED', 'ACTIONED', 'DISMISSED', 'ARCHIVED'];
const PAGE = 25;

/**
 * M41 — Insight Dashboard (§26 / §14). The intelligent-insight surface over the
 * generated insights (T542). Generate an advisory insight (trend / performance /
 * risk / cost / optimization / forecast / strategic), then move it through its
 * lifecycle — acknowledge → action or dismiss. Every insight is advisory only
 * (never auto-actioned). Viewing needs INSIGHT_VIEW, generation needs
 * INSIGHT_GENERATE, lifecycle transitions need INSIGHT_MANAGE.
 */
export function InsightDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<InsightRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [title, setTitle] = useState('');
  const [insightType, setInsightType] = useState('TREND');
  const [sourceType, setSourceType] = useState('DATASET');
  const [severity, setSeverity] = useState('MEDIUM');
  const [recommendation, setRecommendation] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('insightType', typeFilter);
    if (severityFilter) params.set('severity', severityFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: InsightRow[]; total: number }>(`/v1/data-lake/insights?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, severityFilter, statusFilter, page, reload]);

  const generate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m41.insight.titleRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/insights', {
        method: 'POST',
        body: { title: title.trim(), insightType, sourceType, severity, recommendation: recommendation.trim() || null },
        token,
      });
      setNotice(t('m41.insight.generated'));
      setTitle('');
      setRecommendation('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.insight.notAuthGenerate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const transition = async (id: string, verb: 'acknowledge' | 'action' | 'dismiss'): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/insights/${id}/${verb}`, { method: 'POST', body: {}, token });
      setNotice(t(`m41.insight.transitioned.${verb}`));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.insight.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="insight-dashboard">
      <h1>{t('m41.insight.title')}</h1>

      <section aria-label="insight-generate">
        <h2>{t('m41.insight.generate')}</h2>
        <form aria-label="generate-form" onSubmit={(e) => void generate(e)}>
          <label>{t('m41.insight.titleLabel')}<input aria-label="field-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m41.common.type')}
            <select aria-label="field-insight-type" value={insightType} onChange={(e) => setInsightType(e.target.value)}>
              {INSIGHT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.common.source')}
            <select aria-label="field-source-type" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
              {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.common.severity')}
            <select aria-label="field-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.insight.recommendation')}<input aria-label="field-recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-generate">{t('m41.common.generate')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="insight-action-error">{actionError}</p>}
        {notice && <p aria-label="insight-notice">{notice}</p>}
      </section>

      <section aria-label="insight-filters">
        <label>{t('m41.common.type')}
          <select aria-label="filter-insight-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {INSIGHT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m41.common.severity')}
          <select aria-label="filter-severity" value={severityFilter} onChange={(e) => { setSeverityFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('m41.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="insight-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="insight-total">{t('m41.insight.total', { count: total })}</p>
          <DataTable
            ariaLabel="insight-table"
            rows={rows}
            rowKey={(i) => i.insightId}
            emptyMessage={t('m41.insight.empty')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (i) => `insight-code-${i.insightId}`, render: (i) => i.insightCode },
              { key: 'title', header: t('m41.insight.titleLabel'), render: (i) => i.title },
              { key: 'type', header: t('m41.common.type'), render: (i) => i.insightType },
              { key: 'severity', header: t('m41.common.severity'), render: (i) => i.severity },
              { key: 'confidence', header: t('m41.common.confidence'), render: (i) => decimal(i.confidence) },
              { key: 'status', header: t('m41.common.status'), cellAriaLabel: (i) => `insight-status-${i.insightId}`, render: (i) => i.status },
              { key: 'actions', header: t('m41.common.actions'), render: (i) => (
                <>
                  {i.status === 'NEW' && (
                    <Button variant="secondary" type="button" aria-label={`acknowledge-${i.insightId}`} onClick={() => void transition(i.insightId, 'acknowledge')}>{t('m41.insight.acknowledge')}</Button>
                  )}
                  {(i.status === 'NEW' || i.status === 'ACKNOWLEDGED') && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`action-${i.insightId}`} onClick={() => void transition(i.insightId, 'action')}>{t('m41.insight.action')}</Button>
                      <Button variant="secondary" type="button" aria-label={`dismiss-${i.insightId}`} onClick={() => void transition(i.insightId, 'dismiss')}>{t('m41.insight.dismiss')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />

          <nav aria-label="insight-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
